import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, onAuthStateChanged } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';

// Log Firebase initialization start
console.log('Firebase: Starting initialization');

// Verify we have all required configuration values
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
if (missingVars.length > 0) {
  console.error(`Firebase: Missing required configuration: ${missingVars.join(', ')}`);
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

console.log('Firebase: Configuration loaded, initializing services');

// Initialize Firebase with error handling
let app, auth, db, storage, functions, analytics;
try {
  app = !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];
  console.log('Firebase: App initialized');
  
  auth = getAuth(app);
  console.log('Firebase: Auth service initialized');
  
  db = getFirestore(app);
  console.log('Firebase: Firestore service initialized');
  
  storage = getStorage(app);
  console.log('Firebase: Storage service initialized');
  
  functions = getFunctions(app, 'us-central1'); // Explicitly specify region
  console.log('Firebase: Functions service initialized with region us-central1');

  // Initialize Analytics (only in browser environments that support it)
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('Firebase: Analytics initialized');
    } else {
      console.log('Firebase: Analytics not supported in this environment');
    }
  }).catch((err) => {
    console.log('Firebase: Analytics check failed:', err);
  });

  // Connect to local emulators if running on localhost
  if (window.location.hostname === 'localhost') {
    // Temporarily disable emulators to use production services
    // connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    // console.log('Firebase: Connected Functions to local emulator at 127.0.0.1:5001');
    // connectFirestoreEmulator(db, 'localhost', 8086);
    // console.log('Firebase: Connected Firestore to local emulator at localhost:8086');
    console.log('Firebase: Using production services for testing');
  }
  
  // Add an initial auth state listener to verify Auth is working
  const initialAuthCheck = onAuthStateChanged(auth, (user) => {
    console.log('Firebase: Initial auth state check -', user ? 'User is signed in' : 'No user signed in');
    // Unsubscribe immediately after the first check
    initialAuthCheck();
  }, (error) => {
    console.error('Firebase: Error in initial auth check:', error);
  });
  
  console.log('Firebase: All services initialized successfully');
} catch (error) {
  console.error('Firebase: Error during initialization:', error);
}

export { app, db, auth, storage, functions, analytics }; 